import { makeAutoObservable, toJS } from 'mobx'
import { KnowledgebaseArticleFactory, KnowledgebaseCategoryFactory } from './../../../shared/factories'

class KnowledgebaseStore {
  isLoading = false
  isFetching = false
  isSaving = false

  Article = null
  Articles = []
  articlesPagination = { per_page: 50 }
  articlesSearch = ''
  articlesOrderBy = { id: 'DESC' }

  Category = null
  categoryDetail = {}
  Categories = {}
  categoriesPagination = { per_page: 50 }
  categoriesSearch = ''
  categoriesOrderBy = { c_name: 'ASC' }

  constructor() {
    makeAutoObservable(this)
  }

  init = async () => {
    this.isLoading = true
    try {
      const [Articles, Categories] = await Promise.all([
        KnowledgebaseArticleFactory.search({
          pagination: this.articlesPagination,
        }),
        KnowledgebaseCategoryFactory.search({ pagination: false }),
      ])

      this.Articles = (Articles && Articles?.models) || []
      this.articlesPagination = (Articles && Articles?.pagination) || {
        per_page: 50,
      }
      this.Categories = {}
      const categoriesList = Array.isArray(Categories) ? Categories : Categories?.models || []
      categoriesList.forEach(Category => (this.Categories[Category.id()] = Category))
    } catch (e) {
      this.Articles = []
      this.Categories = {}
    } finally {
      this.isLoading = false
    }
  }

  fetchCategoryDetails = async Category => {
    if (!Category.article() || !Category.article().length)
      (await Category.article(true).all()).forEach(Article => Category.article().push(Article))

    this.categoryDetail.articles = Category.article().length
  }

  searchArticles = async ({ search, orderBy, pagination }) => {
    if (!this.isLoading) this.isLoading = true

    search = search === false ? {} : search ? search : this.articlesSearch
    orderBy = orderBy === false ? { id: 'DESC' } : orderBy ? orderBy : this.articlesOrderBy
    pagination = pagination === false ? pagination : pagination ? pagination : this.articlesPagination

    this.articlesSearch = search
    this.articlesOrderBy = orderBy
    this.articlesPagination = pagination

    const [Articles] = await Promise.all([
      KnowledgebaseArticleFactory.search({
        search,
        order_by: orderBy,
        pagination,
      }),
    ])

    this.Articles = (Articles && Articles?.models) || []
    this.articlesPagination = (Articles && Articles?.pagination) || {
      per_page: 50,
    }

    this.isLoading = false
  }

  startNewArticle = () => {
    try {
      const categoryId = this.Category?.id() > 0 ? this.Category.id() : Object.keys(this.Categories || {})[0] ?? null
      this.Article =
        KnowledgebaseArticleFactory.create({
          category_id: categoryId ?? undefined,
        }) || null
    } catch (e) {
      this.Article = null
    }
  }

  searchCategories = async ({ search, orderBy, pagination }) => {
    if (!this.isLoading) this.isLoading = true

    search = search === false ? {} : search ? search : this.categoriesSearch
    orderBy = orderBy === false ? { id: 'DESC' } : orderBy ? orderBy : this.categoriesOrderBy
    pagination = pagination === false ? pagination : pagination ? pagination : this.categoriesPagination

    this.categoriesSearch = search
    this.categoriesOrderBy = orderBy
    this.categoriesPagination = pagination

    const [Categories] = await Promise.all([
      KnowledgebaseCategoryFactory.search({
        search,
        order_by: orderBy,
        pagination,
      }),
    ])
    ;(Array.isArray(Categories)
      ? Categories
      : Categories && Array.isArray(Categories?.models)
      ? Categories.models
      : []
    ).forEach(Category => (this.Categories[Category.id()] = Category))

    this.categoriesPagination = (Categories && Categories?.pagination) || {
      per_page: 50,
    }

    this.isLoading = false
  }

  startNewCategory = async () => {
    this.Category = await KnowledgebaseCategoryFactory.create({ c_name: '' })
  }

  editArticle = async Article => {
    this.Article = Article
  }

  deleteArticle = async Article => {
    try {
      this.isSaving = true

      // Call the delete API
      await Article.delete()

      // Remove the article from the Articles array
      const index = this.Articles.findIndex(a => a.id() === Article.id())
      if (index > -1) {
        this.Articles.splice(index, 1)
      }

      this.isSaving = false
      return true
    } catch (error) {
      this.isSaving = false
      console.error('Failed to delete article:', error)
      throw error
    }
  }

  restoreArticle = async Article => {
    try {
      this.isSaving = true

      // Call the restore API using the service
      await KnowledgebaseArticleFactory.getService().restore(Article.id())

      // Add the article back to the Articles array if it's not already there
      const index = this.Articles.findIndex(a => a.id() === Article.id())
      if (index === -1) {
        this.Articles.push(Article)
      }

      this.isSaving = false
      return true
    } catch (error) {
      this.isSaving = false
      console.error('Failed to restore article:', error)
      throw error
    }
  }

  editCategory = async Category => {
    if (!this.Category || (this.Category && this.Category.id() !== Category.id()))
      await this.fetchCategoryDetails(Category)

    this.Category = Category
  }

  toArray = obj => Object.values(obj && typeof obj === 'object' ? obj : {})
}

export default new KnowledgebaseStore()
